import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Controlled as CodeMirror } from 'react-codemirror2';
import {
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import ValidatorzService from '../../data/validatorz-service';
import { User } from '../../data/auth-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
    accordionSummaryReplacement: {
        background: theme.palette.background.paper,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        // marginBottom: -theme.spacing(2),
        padding: theme.spacing(1),
    }
}));

const empty = {
    created: '',
    created_by: '',
    enabled: true,
    id: 0,
    new: true,
    note: '',
    updated: '',
    updated_by: '',
    filter: {
        action: [],
        filter: ''
    },
};

const Filter = forwardRef((props, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const [filter, setFilter] = useState({
        filtertypes: ['inactive', 'black', 'white'],
        selfiltertype: 'black',
        filters: [
            // {
            //     created: '2021-03-15T00:14:27.457Z',
            //     created_by: 'admin',
            //     enabled: true,
            //     id: 0,
            //     new: false,
            //     note: 'Test',
            //     updated: '2021-03-15T00:14:27.457Z',
            //     updated_by: 'admin',
            //     filter: {
            //         action: [],
            //         filter: 'IP(src_ip, "172.17.11.42")'
            //     },
            // }
        ],
    });
    const [editedFilters, setEditedFilters] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState([]);
    const user = User();

    useImperativeHandle(ref, () => ({
        getFilter: () => {
            return filter;
        },
    }));

    useEffect(() => {
        setFilter(props.filter);
        if (props.filter && props.filter.filters) {
            const filters = props.filter.filters.map((v) => {
                return {
                    name: v.note,
                    filter: v.filter.filter,
                };
            });
            if (!editedFilters.length) {
                setEditedFilters(_cloneDeep(filters));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const handleFilterChange = (e) => {
        const f = { ...filter };
        f.selfiltertype = e.currentTarget.value;
        setFilter(f);
        props.onUpdate(f);
    };

    const handleAddFilter = () => {
        const f = { ...filter };

        if (!f.filters) {
            f.filters = [];
        }

        f.filters.push({
            ..._cloneDeep(empty),
            created: new Date().toISOString(),
            created_by: user.username,
        });

        setFilter(f);

        const fo = [...filtersOpen];
        fo[f.filters.length - 1] = true;
        setFiltersOpen(fo);

        const ef = [...editedFilters];
        ef.push({ name: '', filter: '' });
        setEditedFilters(ef);
    };

    const handleUpdateFilterName = (e, i) => {
        const ef = [...editedFilters];
        ef[i].name = e.target.value;
        setEditedFilters(ef);
    };

    const handleFilterEnable = (e, i) => {
        const f = { ...filter };
        f.filters[i].enabled = e.target.checked;
        setFilter(f);
    };

    const handleFilterUpdate = (value, i) => {
        const ef = [...editedFilters];
        ef[i].filter = value.trimLeft();
        setEditedFilters(ef);
    };

    const handleOpenFilterAccordion = (i) => {
        const fo = [...filtersOpen];
        fo[i] = !fo[i];
        setFiltersOpen(fo);
    };

    const handleSaveFilter = (i) => {
        ValidatorzService.gator(editedFilters[i].filter).then(res => {
            if (res.error) {
                toast.error('Filter is not valid, cannot be saved: ' + res.error);
            } else {
                const f = { ...filter };
                f.filters[i].note = editedFilters[i].name;
                f.filters[i].filter.filter = editedFilters[i].filter;
                f.filters[i].updated = new Date().toISOString();
                f.filters[i].updated_by = user.username;
                setFilter(f);
                props.onUpdate(f);

                const fo = [...filtersOpen];
                fo[i] = false;
                setFiltersOpen(fo);

                if (props.onVerify && props.onVerify.call) {
                    props.onVerify();
                } else {
                    toast.success('Filter validation successful');
                }
            }
        }, err => {
            toast.error(err.error);
        });
    };

    const handleCancelFilterUpdate = (i) => {
        if (!filter.filters[i].note && !filter.filters[i].filter.filter) {
            handleDeleteFilter(i);
            return;
        }

        const ef = [...editedFilters];
        ef[i] = {
            name: filter.filters[i].note,
            filter: filter.filters[i].filter.filter,
        };
        setEditedFilters(ef);

        const fo = [...filtersOpen];
        fo[i] = false;
        setFiltersOpen(fo);
    };

    const handleDeleteFilter = (i) => {
        const f = { ...filter };
        f.filters.splice(i, 1);
        setFilter(f);
        props.onUpdate(f);

        const ef = [...editedFilters];
        ef.splice(i, 1);
        setEditedFilters(ef);

        const fo = [...filtersOpen];
        fo.splice(i, 1);
        setFiltersOpen(fo);
    };

    return (
        <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between">
                <Box>
                    <Typography>Filter(s)</Typography>
                    <RadioGroup row value={filter.selfiltertype} onChange={handleFilterChange}>
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Inactive List"
                            aria-label="inactive"
                            control={<Radio value='inactive' />} />
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Deny List"
                            aria-label="deny"
                            control={<Radio value='black' />} />
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Accept List"
                            aria-label="accept"
                            control={<Radio value='white' />} />
                    </RadioGroup>
                </Box>
                {(props.perms.write && props.policySource !== 'mc2') && <Box pt={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddFilter}
                        disabled={filtersOpen.includes(true)}
                    >
                        <AddIcon /> Filter
                    </Button>
                </Box>}
            </Box>
            <Box mt={1}>
                {filter.filters && filter.filters.map((v, i) => {
                    return (
                        <React.Fragment key={'filter-config-' + i}>
                            <Box className={classes.accordionSummaryReplacement} mb={filtersOpen[i] ? '-16px' : ''}>
                                <Box flexGrow={0.33}>
                                    <TextField
                                        fullWidth
                                        label="Filter Name"
                                        value={editedFilters[i] && editedFilters[i].name}
                                        onChange={(e) => { handleUpdateFilterName(e, i) }}
                                        disabled={!filtersOpen[i] || (!props.perms.write || props.policySource === 'mc2')}
                                    />
                                </Box>
                                <Box display="flex">
                                    <Checkbox
                                        disabled={(!props.perms.write || props.policySource === 'mc2')}
                                        checked={v.enabled}
                                        onChange={(e) => { handleFilterEnable(e, i) }}
                                    />
                                    <Box>
                                        <IconButton disabled={(!props.perms.write || props.policySource === 'mc2')} onClick={() => { handleDeleteFilter(i) }} size="large">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                    <Box>
                                        <IconButton
                                            onClick={() => { handleOpenFilterAccordion(i) }}
                                            disabled={filtersOpen[i]}
                                            size="large">
                                            <EditIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                            <Accordion expanded={filtersOpen[i] || false}>
                                <AccordionSummary style={{ display: 'none' }}></AccordionSummary>
                                <AccordionDetails>
                                    <Box width="100%">
                                        <Box className={classes.codemirrorBox}>
                                            <CodeMirror
                                                readOnly={!props.perms.write}
                                                options={{
                                                    mode: 'gator',
                                                    theme: theme.codeMirror.theme,
                                                    lineNumbers: true,
                                                    lineWrapping: true,
                                                    readOnly: (!props.perms.write || props.policySource === 'mc2'),
                                                    autoCursor: true,
                                                }}
                                                value={editedFilters[i] && editedFilters[i].filter}
                                                onChange={() => {}}
                                                onBeforeChange={(cm, e, value) => { handleFilterUpdate(value, i) }}
                                            />
                                        </Box>
                                        <Box mt={2} display="flex" justifyContent="flex-end">
                                            <Box ml={2}>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={() => { handleCancelFilterUpdate(i) }}
                                                >Cancel</Button>
                                            </Box>
                                            {(props.perms.write || props.policySource !== 'mc2') && <Box ml={2}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => { handleSaveFilter(i) }}
                                                    disabled={(editedFilters[i] && (!editedFilters[i].name || !editedFilters[i].filter))}
                                                >
                                                    Verify Filter
                                                </Button>
                                            </Box>}
                                        </Box>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </React.Fragment>
                    );
                })}
            </Box>
        </Grid>
    );
});

Filter.propTypes = {
    policySource: PropTypes.string,
    filter: PropTypes.object,
    onVerify: PropTypes.func,
    onUpdate: PropTypes.func,
    perms: PropTypes.object,
};

export default Filter;
